import { formatContract } from "../domain/mapping";
import { compareISODate, parseISODate } from "../domain/date";
import { getPairPreset, type DisplayMetric } from "../domain/pair-presets";
import type { ActualEpisodeSeries, ISODate, PairPresetId } from "../domain/types";
import { fitSimpleOls, pearsonCorrelation, sampleStandardDeviation } from "./math";
import { available, notAvailable, type MetricResult } from "./types";

export interface PairedPricePoint {
  readonly date: ISODate;
  readonly legA: number;
  readonly legB: number;
}

export interface ActualEpisodePricePoint extends PairedPricePoint {
  readonly legAContract: string;
  readonly legBContract: string;
}

export interface DerivedSpreadPoint extends PairedPricePoint {
  readonly rawSpread: number;
  readonly ratio: number;
  readonly logRatio: number;
}

/**
 * Low-level numeric primitive. It does not establish source provenance by
 * itself; formal product analysis should enter through analyzeActualEpisode.
 */
export function deriveSpreadSeries(points: readonly PairedPricePoint[]): readonly DerivedSpreadPoint[] {
  return points.map((point) => {
    if (!Number.isFinite(point.legA) || !Number.isFinite(point.legB) || point.legA <= 0 || point.legB <= 0) {
      throw new Error(`Both normalized prices must be finite and positive (${point.date}).`);
    }
    return {
      ...point,
      rawSpread: point.legA - point.legB,
      ratio: point.legA / point.legB,
      logRatio: Math.log(point.legA) - Math.log(point.legB),
    };
  });
}

export interface BetaResidualEstimate {
  readonly alpha: number;
  readonly beta: number;
  readonly rSquared: number;
  readonly residuals: readonly { readonly date: ISODate; readonly value: number }[];
  readonly direction: "positive-is-leg-a-rich";
}

export interface AnalysisMetricSeries {
  readonly metric: DisplayMetric;
  readonly points: readonly { readonly date: ISODate; readonly value: number }[];
  readonly direction: "positive-is-leg-a-rich";
}

export function selectAnalysisMetricSeries(
  derived: readonly DerivedSpreadPoint[],
  betaResidual: MetricResult<BetaResidualEstimate>,
  metric: DisplayMetric,
): MetricResult<AnalysisMetricSeries> {
  if (metric === "beta-residual") {
    if (betaResidual.status === "not-available") return betaResidual;
    return available(
      {
        metric,
        points: betaResidual.value.residuals,
        direction: "positive-is-leg-a-rich",
      },
      betaResidual.sampleSize,
      betaResidual.method,
      betaResidual.warnings,
    );
  }
  const valueFor = (point: DerivedSpreadPoint): number => {
    if (metric === "ratio") return point.ratio;
    if (metric === "log-ratio") return point.logRatio;
    return point.rawSpread;
  };
  return available(
    {
      metric,
      points: derived.map((point) => ({ date: point.date, value: valueFor(point) })),
      direction: "positive-is-leg-a-rich",
    },
    derived.length,
    `${metric} from aligned common-currency/common-unit actual settlements`,
  );
}

export function estimateLogPriceBetaResidual(
  points: readonly PairedPricePoint[],
  minimumSample = 20,
): MetricResult<BetaResidualEstimate> {
  if (points.length < minimumSample) {
    return notAvailable(
      "insufficient-sample",
      `Need at least ${minimumSample} paired price levels.`,
      points.length,
      "OLS: log(Canola) = alpha + beta * log(comparator) + residual",
    );
  }
  if (points.some((point) => point.legA <= 0 || point.legB <= 0 || !Number.isFinite(point.legA + point.legB))) {
    return notAvailable("invalid-input", "Log-price OLS requires finite positive prices.", points.length);
  }
  const x = points.map((point) => Math.log(point.legB));
  const y = points.map((point) => Math.log(point.legA));
  const fit = fitSimpleOls(x, y);
  if (!fit || !Number.isFinite(fit.beta)) {
    return notAvailable("unstable-estimate", "The OLS design is singular or unstable.", points.length);
  }
  return available(
    {
      alpha: fit.alpha,
      beta: fit.beta,
      rSquared: fit.rSquared,
      residuals: points.map((point, index) => ({
        date: point.date,
        value: fit.residuals[index] ?? Number.NaN,
      })),
      direction: "positive-is-leg-a-rich",
    },
    points.length,
    "OLS with intercept on common-currency log prices within one actual contract episode",
  );
}

export interface CorrelationEstimate {
  readonly correlation: number;
  readonly returnObservations: number;
  readonly correlationKind: "daily-log-return";
}

/** Price-level correlation is deliberately not used by this function. */
export function logReturnCorrelation(
  points: readonly PairedPricePoint[],
  minimumReturnSample = 20,
): MetricResult<CorrelationEstimate> {
  const legAReturns: number[] = [];
  const legBReturns: number[] = [];
  for (let index = 1; index < points.length; index += 1) {
    const current = points[index];
    const previous = points[index - 1];
    if (!current || !previous || current.legA <= 0 || current.legB <= 0 || previous.legA <= 0 || previous.legB <= 0) {
      return notAvailable("invalid-input", "Log returns require finite positive prices.", legAReturns.length);
    }
    legAReturns.push(Math.log(current.legA / previous.legA));
    legBReturns.push(Math.log(current.legB / previous.legB));
  }
  if (legAReturns.length < minimumReturnSample) {
    return notAvailable(
      "insufficient-sample",
      `Need at least ${minimumReturnSample} paired daily log returns.`,
      legAReturns.length,
      "Pearson correlation of daily log returns",
    );
  }
  const correlation = pearsonCorrelation(legAReturns, legBReturns);
  if (!Number.isFinite(correlation)) {
    return notAvailable("zero-variance", "At least one return series has zero variance.", legAReturns.length);
  }
  return available(
    { correlation, returnObservations: legAReturns.length, correlationKind: "daily-log-return" },
    legAReturns.length,
    "Pearson correlation of within-episode daily log returns (not price levels)",
  );
}

export interface RollingPoint {
  readonly index: number;
  readonly value: number | null;
  readonly sampleSize: number;
}

export function rollingZScore(values: readonly number[], window: number): readonly RollingPoint[] {
  if (!Number.isInteger(window) || window < 2) throw new Error("window must be an integer >= 2.");
  return values.map((value, index) => {
    const start = Math.max(0, index - window + 1);
    const sample = values.slice(start, index + 1);
    if (sample.length < window || sample.some((item) => !Number.isFinite(item))) {
      return { index, value: null, sampleSize: sample.length };
    }
    const center = sample.reduce((sum, item) => sum + item, 0) / sample.length;
    const deviation = sampleStandardDeviation(sample);
    return {
      index,
      value: Number.isFinite(deviation) && deviation > 0 ? (value - center) / deviation : null,
      sampleSize: sample.length,
    };
  });
}

export function rollingSpreadVolatility(
  spreadLevels: readonly number[],
  window: number,
  annualization = Math.sqrt(252),
): readonly RollingPoint[] {
  if (!Number.isInteger(window) || window < 2) throw new Error("window must be an integer >= 2.");
  const changes = spreadLevels.map((value, index) =>
    index === 0 ? null : value - (spreadLevels[index - 1] ?? value),
  );
  return spreadLevels.map((_, index) => {
    const endChanges = changes.slice(Math.max(1, index - window + 2), index + 1).filter(
      (value): value is number => value !== null,
    );
    if (endChanges.length < window - 1 || endChanges.some((value) => !Number.isFinite(value))) {
      return { index, value: null, sampleSize: endChanges.length };
    }
    const deviation = sampleStandardDeviation(endChanges);
    return {
      index,
      value: Number.isFinite(deviation) ? deviation * annualization : null,
      sampleSize: endChanges.length,
    };
  });
}

export interface ActualEpisodeAnalysis {
  readonly scope: "actual-episode";
  readonly episodeId: string;
  readonly pairPresetId: PairPresetId;
  readonly primaryStatisticalMetric: DisplayMetric;
  readonly selectedMetric: DisplayMetric;
  readonly metricSeries: MetricResult<AnalysisMetricSeries>;
  readonly rawSpread: readonly number[];
  readonly ratio: readonly number[];
  readonly logRatio: readonly number[];
  readonly betaResidual: MetricResult<BetaResidualEstimate>;
  readonly returnCorrelation: MetricResult<CorrelationEstimate>;
  readonly localZ: readonly RollingPoint[];
  readonly spreadVolatility: readonly RollingPoint[];
}

export interface ActualEpisodeAnalysisOptions {
  readonly pairPresetId: PairPresetId;
  readonly metric?: DisplayMetric;
  readonly minimumSample?: number;
  readonly zWindow?: number;
  readonly volatilityWindow?: number;
}

export type LegacyActualEpisodeAnalysisOptions = Omit<ActualEpisodeAnalysisOptions, "pairPresetId"> & {
  readonly pairPresetId?: never;
};

export function analyzeActualEpisode(
  episode: ActualEpisodeSeries<ActualEpisodePricePoint>,
  options: ActualEpisodeAnalysisOptions,
): ActualEpisodeAnalysis;
/** @deprecated Pass ActualEpisodeAnalysisOptions with an explicit pairPresetId. */
export function analyzeActualEpisode(
  episode: ActualEpisodeSeries<ActualEpisodePricePoint>,
  options?: LegacyActualEpisodeAnalysisOptions,
): ActualEpisodeAnalysis;
export function analyzeActualEpisode(
  episode: ActualEpisodeSeries<ActualEpisodePricePoint>,
  options: ActualEpisodeAnalysisOptions | LegacyActualEpisodeAnalysisOptions = {},
): ActualEpisodeAnalysis {
  if (episode.backAdjusted !== false || episode.source !== "actual-settlements") {
    throw new Error("Formal current-episode analysis requires unadjusted actual settlements.");
  }
  const pairPresetId = options.pairPresetId ?? episode.pair.pairId;
  if (episode.pair.pairId !== pairPresetId) {
    throw new Error("Explicit pair preset does not match the actual episode pair.");
  }
  const preset = getPairPreset(pairPresetId);
  const selectedMetric = options.metric ?? preset.primaryStatisticalMetric;
  if (!preset.permittedMetrics.includes(selectedMetric)) {
    throw new Error(`${selectedMetric} is not permitted for ${pairPresetId}.`);
  }
  const expectedLegA = formatContract(episode.pair.legA);
  const expectedLegB = formatContract(episode.pair.legB);
  if (episode.points.some((point) => point.legAContract !== expectedLegA || point.legBContract !== expectedLegB)) {
    throw new Error("An actual episode cannot contain observations from a different mapped contract pair.");
  }
  episode.points.forEach((point, index) => {
    parseISODate(point.date);
    const previous = episode.points[index - 1];
    if (previous && compareISODate(previous.date, point.date) >= 0) {
      throw new Error("An actual episode must be strictly date-ordered.");
    }
  });
  if (new Set(episode.points.map((point) => point.date)).size !== episode.points.length) {
    throw new Error("An actual episode cannot contain duplicate dates.");
  }
  const derived = deriveSpreadSeries(episode.points);
  const minimumSample = options.minimumSample ?? 20;
  const betaResidual = estimateLogPriceBetaResidual(episode.points, minimumSample);
  const metricSeries = selectAnalysisMetricSeries(derived, betaResidual, selectedMetric);
  const zWindow = options.zWindow ?? 60;
  return {
    scope: "actual-episode",
    episodeId: episode.episodeId,
    pairPresetId,
    primaryStatisticalMetric: preset.primaryStatisticalMetric,
    selectedMetric,
    metricSeries,
    rawSpread: derived.map((point) => point.rawSpread),
    ratio: derived.map((point) => point.ratio),
    logRatio: derived.map((point) => point.logRatio),
    betaResidual,
    returnCorrelation: logReturnCorrelation(episode.points, Math.max(2, minimumSample - 1)),
    localZ: metricSeries.status === "available"
      ? rollingZScore(metricSeries.value.points.map((point) => point.value), zWindow)
      : episode.points.map((_, index) => ({ index, value: null, sampleSize: 0 })),
    spreadVolatility: rollingSpreadVolatility(
      derived.map((point) => point.rawSpread),
      options.volatilityWindow ?? 20,
    ),
  };
}
